// =============================================================================
// Inventory Management — the module pool of equipment bought ahead of projects.
//
// Mirrors the operational tracker Luminia runs the RIC safe-harbor fleet from
// ("RIC Solar Inventory Logistics Tracker"), so the shapes here are the tracker's own, not an invention:
//
//   Warehouse ──< Lot (one purchase order / vendor contract) ──< Pallet ──< Module (serial + flash test)
//                                                                  │
//   Site (demand: one roof) ──< Delivery (warehouse → site) ───────┘
//                                   └──> Shipment (outbound leg) + Reduction (the accounting entry)
//
// A pallet is indivisible and belongs to at most one site: assigning it is planning, and nothing leaves
// the books until a Delivery reaches 'delivered', which posts the Reduction rows the finance workbook
// has always been kept by hand. Sites are NOT project-management projects — the tracker carries ~3.5x
// more sites than the PM module has projects — so they live here and link to a project when one exists.
// =============================================================================

import { Mongo, MongoInternals } from 'meteor/mongo';
import { Meteor } from 'meteor/meteor';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { DeclareMethod } from '/imports/methods';
import { Roles, accountHasAccounting } from '/imports/api/accounts';
import { validateBoolean, validateNumber, validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { log } from '/imports/api/logs';

type UserLike = { isSuperUser?: boolean } | null | undefined;
type AccountLike = { roles?: string[] } | null | undefined;

/**
 * Who may see the equipment pool. Its own grant, NOT implied by the project-management role: a project
 * stakeholder has no business reading what the org holds above the project level.
 * @param user - the viewing user
 * @param account - that user's account in the current org
 * @returns true when inventory is visible
 */
export const inventoryCanAccess = (user?: UserLike, account?: AccountLike): boolean =>
  !!user?.isSuperUser || !!account?.roles?.includes(Roles.inventory);

/**
 * Who may move stock — assign pallets, run deliveries, correct pallet records.
 * @param user - the acting user
 * @param account - that user's account in the current org
 * @returns true when inventory may be edited
 */
export const inventoryCanEdit = (user?: UserLike, account?: AccountLike): boolean =>
  !!user?.isSuperUser || (!!account?.roles?.includes(Roles.inventory) && !!account?.roles?.includes(Roles.admin));

/**
 * Who may fill in the tax treatment of a posted reduction. Finance, not operations.
 * @param user - the acting user
 * @param account - that user's account in the current org
 * @returns true when the ledger's tax columns may be edited
 */
export const inventoryCanEditFinance = (user?: UserLike, account?: AccountLike): boolean =>
  !!user?.isSuperUser || (!!account?.roles?.includes(Roles.inventory) && accountHasAccounting(account));

// --- Vocabularies. These are the tracker's own dropdown lists; keeping them identical is what lets a
//     re-import reconcile against the workbook instead of guessing at a mapping. ---

export const inventoryPalletStatuses = [
  'onOrder', 'inStock', 'reserved', 'allocated', 'stagedForShipment', 'inTransit', 'delivered', 'installed',
  'damaged', 'rmaReturn', 'lost',
] as const;
export type InventoryPalletStatus = typeof inventoryPalletStatuses[number];

/**
 * Statuses an editor sets by hand. Everything else is a consequence: the delivery workflow moves a
 * pallet through staged → in transit → delivered → installed, and assignment deliberately changes no
 * status at all, so this column keeps meaning the PHYSICAL state of the pallet. 'reserved' and
 * 'allocated' exist because the source tracker's vocabulary has them; nothing here sets them.
 */
export const inventoryManualPalletStatuses: InventoryPalletStatus[] = ['inStock', 'damaged', 'rmaReturn', 'lost'];

/** Pallets counted as physically held by the warehouse (the tracker's "on hand" rule). */
export const inventoryOnHandStatuses: InventoryPalletStatus[] = ['inStock', 'reserved', 'allocated', 'stagedForShipment'];

/** Pallets that have left the warehouse. */
export const inventoryShippedStatuses: InventoryPalletStatus[] = ['inTransit', 'delivered', 'installed'];

export const inventoryDeliveryStatuses = [
  'notRequested', 'requested', 'scheduled', 'staged', 'inTransit', 'delivered', 'installed', 'exception',
] as const;
export type InventoryDeliveryStatus = typeof inventoryDeliveryStatuses[number];

/**
 * The happy path, in order. 'exception' sits outside it and is reachable from anywhere.
 * 'notRequested' is the state a delivery arrives in from the tracker when dates were collected before
 * anyone raised the request; the app itself never creates one there.
 */
export const inventoryDeliveryFlow: InventoryDeliveryStatus[] = [
  'notRequested', 'requested', 'scheduled', 'staged', 'inTransit', 'delivered', 'installed',
];

/** The pallet status each delivery state implies, where it implies one. */
export const inventoryDeliveryPalletStatus = (status: InventoryDeliveryStatus): InventoryPalletStatus | undefined => {
  switch (status) {
    case 'notRequested': case 'requested': case 'scheduled': return 'inStock';
    case 'staged': return 'stagedForShipment';
    case 'inTransit': return 'inTransit';
    case 'delivered': return 'delivered';
    case 'installed': return 'installed';
    default: return undefined;
  }
};

export const inventoryShipmentStatuses = ['scheduled', 'inTransit', 'delivered', 'delayed', 'exception', 'cancelled'] as const;
export type InventoryShipmentStatus = typeof inventoryShipmentStatuses[number];

export const inventoryAllocationStatuses = ['unallocated', 'partial', 'complete'] as const;
export type InventoryAllocationStatus = typeof inventoryAllocationStatuses[number];

export const inventorySiteStatuses = ['inProgress', 'onHold', 'cancelled'] as const;
export type InventorySiteStatus = typeof inventorySiteStatuses[number];

export const inventoryFeocStatuses = ['nonFeoc', 'feoc', 'tbd'] as const;
export type InventoryFeocStatus = typeof inventoryFeocStatuses[number];

/** Pallets per truck on the outbound leg — the tracker's planning figure. */
export const inventoryPalletsPerTruck = 16;

/** The fleet standard; a lot may override it (the 570 W carry-over stock ships 31 to a pallet). */
export const inventoryDefaultModulesPerPallet = 36;

// --- Collections ---

export interface InventoryWarehouse extends Doc {
  orgId?: string;
  code: string;                    // 'WH-1'
  label: string;                   // how the finance workbook names it: 'Delaware Inventory'
  address: string;
  city: string;
  state: string;
  zip?: string;
  // A laydown yard is somebody else's parking lot: no operator, no storage invoices, no capacity ceiling.
  isLaydownYard?: boolean;
  operator?: string;
  operatorProjectNumber?: string;  // the operator's own job number, printed on their receipts and invoices
  operatorWarehouseName?: string;  // how the operator names the site on those documents
  contactName?: string;
  contactPhone?: string;
  capacityUnits?: number;
  receivingWindow?: string;
  rates?: InventoryWarehouseRates;
  scheduleAPallets?: number;
  scheduleAMw?: number;
  storageType?: string;
  bonded?: boolean;
  notes?: string;
}

export interface InventoryWarehouseRates {
  inFee?: number;
  outFee?: number;
  storagePerPalletMonth?: number;
  scanFee?: number;
  dunnagePerTruck?: number;
  laborPerHour?: number;
  overtimePerHour?: number;
  monthlyMinimum?: number;
  escalatorPct?: number;
  escalatorEffective?: Date;
}

export interface InventoryLot extends Doc {
  orgId?: string;
  poNumber?: string;               // absent on carry-over stock bought under an older contract
  contractNumber: string;
  vendorName: string;
  vendorAddress?: string;
  vendorCity?: string;
  vendorState?: string;
  vendorZip?: string;
  vendorInvoiceNumbers?: string[];
  shipToWarehouseId: string;
  equipmentType: string;
  moduleModel: string;
  wattage: number;
  modulesOrdered: number;
  modulesPerPallet: number;
  palletsOrdered: number;
  pricePerWatt?: number;
  costPerUnit?: number;            // absent → no documented cost basis for this stock
  salesTaxRate?: number;
  edd?: Date;                      // earliest delivery date
  gdd?: Date;                      // guaranteed delivery date
  paymentTerms?: string;
  shippingTerms?: string;
  domesticContentPct?: number;
  feocStatus?: InventoryFeocStatus;
  // High-water mark for this lot's reduction ordinals. Server-allocated and never rewound: a reversed
  // delivery deletes its ledger rows, and handing the next one the same number would make two different
  // postings share an identity in anything already exported.
  reductionSeq?: number;
  notes?: string;
}

/** What the warehouse operator scanned when the pallet arrived. */
export interface InventoryPalletReceipt {
  transNumber?: string;
  dateReceived?: Date;
  shipmentRef?: string;
  container?: string;
  bol?: string;
  receiveItemId?: string;
  cuFt?: number;
  lbs?: number;
  hold?: boolean;
}

export interface InventoryPallet extends Doc {
  orgId?: string;
  palletNumber: string;
  // True while the pallet carries a placeholder label because the vendor's flash-test file has not landed.
  provisional?: boolean;
  lotId: string;
  poNumber?: string;
  contractNumber: string;
  warehouseId: string;
  equipmentType: string;
  moduleModel: string;
  wattage: number;
  modules: number;
  domesticContentPct?: number;
  feocStatus?: InventoryFeocStatus;
  unitCost?: number;
  status: InventoryPalletStatus;
  assignedSiteId?: string;
  assignedAt?: Date;
  assignedByName?: string;
  deliveryId?: string;
  receipt?: InventoryPalletReceipt;
  warrantyExpiration?: Date;
  notes?: string;
  serialCount?: number;            // denormalized count of InventoryModules, so the register needs no join
}

export interface InventoryModuleFlash {
  pmax?: number;
  voc?: number;
  isc?: number;
  vm?: number;
  im?: number;
  ff?: number;
}

export interface InventoryModule extends Doc {
  orgId?: string;
  serial: string;
  palletId: string;
  palletNumber: string;
  position?: number;
  moduleModel?: string;
  nameplateW?: number;
  flash?: InventoryModuleFlash;
  container?: string;
  bol?: string;
  poNumber?: string;
  contractNumber?: string;
  warehouseId?: string;
  batch?: string;
  plant?: string;
  deliveryNumber?: string;
  mfgEntryDate?: Date;
  receivedDate?: Date;
}

export interface InventorySite extends Doc {
  orgId?: string;
  siteId: string;                  // the portfolio's own property number, which is what every party quotes
  projectManagementProjectId?: string;
  projectStatus?: InventorySiteStatus;
  tenant?: string;
  propertyName: string;
  address?: string;
  state?: string;
  region?: string;
  closestWarehouseId?: string;
  group?: string;
  moduleModel?: string;
  wattage?: number;
  lntpSizeKw?: number;             // the system size the funded work order was written against
  lntpAmount?: number;
  equipmentWoSizeKw?: number;
  equipmentWoAmount?: number;
  noEquipmentWo?: boolean;         // modules funded under the LNTP work order instead
  finalDesignKw?: number;          // latest design, where one exists
  designSizeOverrideKw?: number;
  notes?: string;
}

export interface InventoryDelivery extends Doc {
  orgId?: string;
  siteId: string;
  status: InventoryDeliveryStatus;
  requestedAt?: Date;
  requestedByName?: string;
  scheduledMobilizationDate?: Date;
  actualShipDate?: Date;
  arrivedAt?: Date;
  receivedBy?: string;
  trucks?: number;
  palletIds: string[];
  warehouseIds?: string[];
  bol?: string;
  carrier?: string;
  notes?: string;
}

export interface InventoryShipment extends Doc {
  orgId?: string;
  shipmentId: string;
  direction: 'inbound' | 'outbound' | 'transfer';
  poNumber?: string;
  contractNumber?: string;
  outboundDate?: Date;
  expectedDelivery?: Date;
  inboundDate?: Date;
  origin?: string;
  destinationWarehouseId?: string;
  destinationSiteId?: string;
  carrier?: string;
  pallets?: number;
  trucks?: number;
  units?: number;
  equipmentSummary?: string;
  status: InventoryShipmentStatus;
  bol?: string;
  notes?: string;
  deliveryId?: string;
}

// --- The accounting entry a delivery posts: modules leaving a lot's book value for a customer site.
//     Quantity, price and amount are snapshots of the delivery and are never editable; the tax columns
//     are the finance team's to fill in afterwards. ---
export interface InventoryReduction extends Doc {
  orgId?: string;
  lotId: string;
  warehouseId?: string;
  deliveryId?: string;
  siteId?: string;
  count: number;                   // per-lot ordinal, never reused
  customer?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  date: Date;
  quantity: number;
  price: number;                   // tax-inclusive price per unit at purchase, as at posting
  amount: number;
  taxJurisdiction?: string;
  salesTaxPaid?: number;
  useTaxDue?: boolean;
  useTaxRate?: number;
  taxLiability?: number;
  paidDate?: Date;
  note?: string;
}

export interface InventoryWarehouseInvoiceLine {
  serviceCategory?: string;
  item?: string;
  qty?: number;
  unitPrice?: number;
  amount?: number;
  contractRate?: number;
  description?: string;
}

export interface InventoryWarehouseInvoice extends Doc {
  orgId?: string;
  invoiceNumber: string;
  invoiceDate?: Date;
  vendor?: string;
  warehouseId?: string;
  operatorProjectNumber?: string;
  projectName?: string;
  servicePeriodStart?: Date;
  servicePeriodEnd?: Date;
  terms?: string;
  dueDate?: Date;
  subtotal?: number;
  tax?: number;
  total?: number;
  amountPaid?: number;
  status?: 'paid' | 'unpaid';
  poNumber?: string;
  projectManager?: string;
  salesRep?: string;
  memo?: string;
  notes?: string;
  lines?: InventoryWarehouseInvoiceLine[];
}

export interface InventoryEvent extends Doc {
  orgId?: string;
  kind: string;
  palletId?: string;
  palletNumber?: string;
  siteId?: string;
  deliveryId?: string;
  lotId?: string;
  reductionId?: string;
  projectManagementProjectId?: string;
  at: Date;
  byUserId?: string;
  byName?: string;
  label?: string;
  details?: { [key: string]: string | number | boolean | Date | null | undefined };
}

export const InventoryWarehouses = new Mongo.Collection<InventoryWarehouse>('inventoryWarehouses');
export const InventoryLots = new Mongo.Collection<InventoryLot>('inventoryLots');
export const InventoryPallets = new Mongo.Collection<InventoryPallet>('inventoryPallets');
export const InventoryModules = new Mongo.Collection<InventoryModule>('inventoryModules');
export const InventorySites = new Mongo.Collection<InventorySite>('inventorySites');
export const InventoryDeliveries = new Mongo.Collection<InventoryDelivery>('inventoryDeliveries');
export const InventoryShipments = new Mongo.Collection<InventoryShipment>('inventoryShipments');
export const InventoryReductions = new Mongo.Collection<InventoryReduction>('inventoryReductions');
export const InventoryWarehouseInvoices = new Mongo.Collection<InventoryWarehouseInvoice>('inventoryWarehouseInvoices');
export const InventoryEvents = new Mongo.Collection<InventoryEvent>('inventoryEvents');

// --- Labels ---

/**
 * Display label for a pallet status.
 * @param status - pallet status key
 * @returns the human label
 */
export const inventoryPalletStatusLabel = (status?: InventoryPalletStatus): string => {
  switch (status) {
    case 'onOrder': return strings.InventoryStatusOnOrder();
    case 'inStock': return strings.InventoryStatusInStock();
    case 'reserved': return strings.InventoryStatusReserved();
    case 'allocated': return strings.InventoryStatusAllocated();
    case 'stagedForShipment': return strings.InventoryStatusStaged();
    case 'inTransit': return strings.InventoryStatusInTransit();
    case 'delivered': return strings.InventoryStatusDelivered();
    case 'installed': return strings.InventoryStatusInstalled();
    case 'damaged': return strings.InventoryStatusDamaged();
    case 'rmaReturn': return strings.InventoryStatusRmaReturn();
    case 'lost': return strings.InventoryStatusLost();
    default: return '';
  }
};

/**
 * Display label for a delivery status.
 * @param status - delivery status key
 * @returns the human label
 */
export const inventoryDeliveryStatusLabel = (status?: InventoryDeliveryStatus): string => {
  switch (status) {
    case 'requested': return strings.InventoryDeliveryRequested();
    case 'scheduled': return strings.InventoryDeliveryScheduled();
    case 'staged': return strings.InventoryDeliveryStaged();
    case 'inTransit': return strings.InventoryStatusInTransit();
    case 'delivered': return strings.InventoryStatusDelivered();
    case 'installed': return strings.InventoryStatusInstalled();
    case 'exception': return strings.InventoryDeliveryException();
    default: return strings.InventoryDeliveryNotRequested();
  }
};

/**
 * Display label for a shipment status.
 * @param status - shipment status key
 * @returns the human label
 */
export const inventoryShipmentStatusLabel = (status?: InventoryShipmentStatus): string => {
  switch (status) {
    case 'scheduled': return strings.InventoryDeliveryScheduled();
    case 'inTransit': return strings.InventoryStatusInTransit();
    case 'delivered': return strings.InventoryStatusDelivered();
    case 'delayed': return strings.InventoryShipmentDelayed();
    case 'exception': return strings.InventoryDeliveryException();
    case 'cancelled': return strings.Canceled();
    default: return '';
  }
};

/**
 * Display label for a site's allocation state.
 * @param status - allocation status key
 * @returns the human label
 */
export const inventoryAllocationStatusLabel = (status?: InventoryAllocationStatus): string => {
  switch (status) {
    case 'complete': return strings.Complete();
    case 'partial': return strings.InventoryAllocationPartial();
    default: return strings.InventoryAllocationUnallocated();
  }
};

/**
 * Display label for a site's project status.
 * @param status - site status key
 * @returns the human label
 */
export const inventorySiteStatusLabel = (status?: InventorySiteStatus): string => {
  switch (status) {
    case 'onHold': return strings.OnHold();
    case 'cancelled': return strings.Canceled();
    default: return strings.InProgress();
  }
};

/**
 * Display label for a FEOC (foreign entity of concern) determination.
 * @param status - FEOC status key
 * @returns the human label
 */
export const inventoryFeocStatusLabel = (status?: InventoryFeocStatus): string => {
  switch (status) {
    case 'nonFeoc': return strings.InventoryFeocNon();
    case 'feoc': return strings.InventoryFeocYes();
    default: return strings.InventoryFeocTbd();
  }
};

/**
 * Display label for a shipment direction.
 * @param direction - shipment direction key
 * @returns the human label
 */
export const inventoryShipmentDirectionLabel = (direction?: string): string => {
  switch (direction) {
    case 'outbound': return strings.InventoryDirectionOutbound();
    case 'transfer': return strings.InventoryDirectionTransfer();
    default: return strings.InventoryDirectionInbound();
  }
};

// --- Derived figures. Everything below is computed, never stored: the tracker derives the same numbers
//     with formulas, and storing them is how the two halves of a system drift apart. ---

/** Rounds to cents the way money has to be compared, avoiding float dust in reconciliations. */
const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Rounds up the way a spreadsheet does. A kW figure times 1000 divided by a wattage lands a hair above
 * a whole number in binary floating point (64.9 kW at 590 W gives 110.00000000000002), and a naive
 * ceiling then orders a pallet nobody needs. Trimming to twelve significant digits first matches the
 * precision the source workbook calculates at.
 */
const ceilExact = (n: number): number => Math.ceil(Number(n.toPrecision(12)));

/** Pre-tax cost of everything a lot ordered. */
export const inventoryLotBasePrice = (lot: Pick<InventoryLot, 'modulesOrdered' | 'costPerUnit'>): number =>
  (lot.modulesOrdered || 0) * (lot.costPerUnit || 0);

/** Sales tax charged on the lot at purchase. */
export const inventoryLotSalesTax = (lot: Pick<InventoryLot, 'modulesOrdered' | 'costPerUnit' | 'salesTaxRate'>): number =>
  inventoryLotBasePrice(lot) * (lot.salesTaxRate || 0);

/** What the lot cost all-in. */
export const inventoryLotTotalCost = (lot: Pick<InventoryLot, 'modulesOrdered' | 'costPerUnit' | 'salesTaxRate'>): number =>
  inventoryLotBasePrice(lot) + inventoryLotSalesTax(lot);

/**
 * Tax-inclusive price of one module as purchased — the rate every reduction is valued at, and the one
 * figure the finance workbook carries that the pre-tax unit cost cannot reproduce.
 */
export const inventoryLotLandedUnitPrice = (lot: Pick<InventoryLot, 'modulesOrdered' | 'costPerUnit' | 'salesTaxRate'>): number =>
  lot.modulesOrdered ? inventoryLotTotalCost(lot) / lot.modulesOrdered : 0;

/** Nameplate megawatts a lot represents. */
export const inventoryLotMw = (lot: Pick<InventoryLot, 'modulesOrdered' | 'wattage'>): number =>
  ((lot.modulesOrdered || 0) * (lot.wattage || 0)) / 1e6;

/** Pre-tax value of the modules sitting on one pallet. */
export const inventoryPalletValue = (pallet: Pick<InventoryPallet, 'modules' | 'unitCost'>): number =>
  (pallet.modules || 0) * (pallet.unitCost || 0);

/** Book units left in a lot after the reductions posted against it. */
export const inventoryLotBookUnits = (lot: Pick<InventoryLot, 'modulesOrdered'>, reducedUnits: number): number =>
  (lot.modulesOrdered || 0) - reducedUnits;

/** Book value left in a lot after the reductions posted against it. */
export const inventoryLotBookValue = (
  lot: Pick<InventoryLot, 'modulesOrdered' | 'costPerUnit' | 'salesTaxRate'>, reducedAmount: number,
): number => round2(inventoryLotTotalCost(lot)) - reducedAmount;

/**
 * Modules a site needs, on the basis the funded work order was written against.
 * @param site - the site's sizing fields
 * @returns module count, or undefined when the site has no work-order size yet
 */
export const inventorySiteModulesRequired = (site: Pick<InventorySite, 'lntpSizeKw' | 'wattage'>): number | undefined => {
  if (!site.lntpSizeKw || !site.wattage) return undefined;
  return ceilExact((site.lntpSizeKw * 1000) / site.wattage);
};

/** The design the site would be built to today, which may differ from what was funded. */
export const inventorySiteDesignKw = (site: Pick<InventorySite, 'designSizeOverrideKw' | 'finalDesignKw' | 'lntpSizeKw'>): number | undefined =>
  site.designSizeOverrideKw ?? site.finalDesignKw ?? site.lntpSizeKw;

/** Modules the current design would need, which the requirement above deliberately does not follow. */
export const inventorySiteModulesForDesign = (site: Pick<InventorySite, 'designSizeOverrideKw' | 'finalDesignKw' | 'lntpSizeKw' | 'wattage'>): number | undefined => {
  const kw = inventorySiteDesignKw(site);
  if (!kw || !site.wattage) return undefined;
  return ceilExact((kw * 1000) / site.wattage);
};

/** Whole pallets a module count needs. */
export const inventoryPalletsFor = (modules?: number, modulesPerPallet = inventoryDefaultModulesPerPallet): number | undefined =>
  modules == null ? undefined : ceilExact(modules / (modulesPerPallet || inventoryDefaultModulesPerPallet));

/** Trucks an outbound pallet count needs. */
export const inventoryTrucksFor = (pallets?: number): number | undefined =>
  pallets == null ? undefined : ceilExact(pallets / inventoryPalletsPerTruck);

/**
 * How far a site's allocation has got. Over-allocation still reads complete — the surplus shows as a
 * negative balance rather than an error, because leftovers on a roof are a real and accepted outcome.
 * @param required - modules the site needs
 * @param allocated - modules assigned to it
 * @returns allocation status
 */
export const inventoryAllocationStatus = (required?: number, allocated = 0): InventoryAllocationStatus => {
  if (!allocated) return 'unallocated';
  if (required == null || allocated >= required) return 'complete';
  return 'partial';
};

/**
 * Use tax owed on a reduction, net of the sales tax already paid at purchase.
 * NOTE: the source workbook's own formula was lost when it was saved, so this is the reading finance
 * described and not a transcription. Changing the rule means changing it here only.
 * @param reduction - the posted quantity and its use-tax determination
 * @param costPerUnit - the lot's pre-tax unit cost
 * @returns tax liability in dollars
 */
export const inventoryTaxLiability = (
  reduction: Pick<InventoryReduction, 'quantity' | 'useTaxDue' | 'useTaxRate' | 'salesTaxPaid'>, costPerUnit = 0,
): number => {
  if (!reduction.useTaxDue) return 0;
  const due = round2((reduction.quantity || 0) * costPerUnit * (reduction.useTaxRate || 0));
  return Math.max(0, round2(due - (reduction.salesTaxPaid || 0)));
};

/** Sales tax already paid at purchase on the modules a reduction removes. */
export const inventorySalesTaxPaid = (quantity: number, costPerUnit = 0, salesTaxRate = 0): number =>
  round2(quantity * costPerUnit * salesTaxRate);

/** Money for inventory screens: whole dollars by default, cents where the ledger needs them. */
export const inventoryMoney = (n?: number, cents?: boolean): string => (
  n == null ? '' : n.toLocaleString(undefined, {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0,
  })
);

/** Counts, with the thousands separators the figures are read with. */
export const inventoryCount = (n?: number): string => (n == null ? '' : n.toLocaleString());

// --- Row shapes the server composes for the screens (a pallet row carries its lot and site labels so
//     the register renders without a join on the client). ---

export interface InventoryPalletRow extends InventoryPallet {
  warehouseCode?: string;
  siteName?: string;
  siteProjectManagementProjectId?: string;
  deliveryStatus?: InventoryDeliveryStatus;
  lotLabel?: string;
}

export interface InventorySiteRow extends InventorySite {
  closestWarehouseCode?: string;
  modulesRequired?: number;
  palletsRequired?: number;
  modulesForDesign?: number;
  modulesAllocated: number;
  palletsAllocated: number;
  moduleBalance?: number;
  allocationStatus: InventoryAllocationStatus;
  trucksRequired?: number;
  deliveryId?: string;
  deliveryStatus?: InventoryDeliveryStatus;
  deliveryRequestedAt?: Date;
  deliveryRequestedByName?: string;
  deliveryScheduledMobilizationDate?: Date;
  deliveryActualShipDate?: Date;
  deliveryArrivedAt?: Date;
  deliveryPallets?: number;
  arrivalVsMobilizationDays?: number;
  projectName?: string;
}

export interface InventoryLotRow extends InventoryLot {
  warehouseCode?: string;
  warehouseLabel?: string;
  basePrice: number;
  salesTax: number;
  totalCost: number;
  landedUnitPrice: number;
  mw: number;
  palletsLoaded: number;
  modulesLoaded: number;
  modulesAllocated: number;
  modulesRemaining: number;
  palletsAllocated: number;
  palletsRemaining: number;
  reducedUnits: number;
  reducedAmount: number;
  bookUnits: number;
  bookValue: number;
}

export interface InventoryDeliveryRow extends InventoryDelivery {
  siteName?: string;
  tenant?: string;
  modules: number;
  pallets: number;
  warehouseCodes: string[];
  arrivalVsMobilizationDays?: number;
  projectManagementProjectId?: string;
}

export interface InventoryWarehouseRow extends InventoryWarehouse {
  lineItems: number;
  unitsOnHand: number;
  unitsTotal: number;
  value: number;
  palletsOnHand: number;
  palletsAllocated: number;
  palletsAvailable: number;
  modulesAvailable: number;
  utilizationPct?: number;
  invoicedTotal: number;
}

export interface InventoryModuleRow extends InventoryModule {
  siteId?: string;
  siteName?: string;
  palletStatus?: InventoryPalletStatus;
}

// --- Dashboard ---

export interface InventoryDashboardData {
  snapshot: {
    lineItems: number;
    unitsTotal: number;
    unitsOnHand: number;
    unitsOnOrder: number;
    valueOnHand: number;
    valueTotal: number;
    pallets: number;
  };
  procurement: {
    modulesOrdered: number;
    mw: number;
    basePrice: number;
    salesTax: number;
    totalCost: number;
    palletsOrdered: number;
    documentedDomesticContentModules: number;
  };
  byWarehouse: {
    warehouseId: string; code: string; label: string; address: string;
    lineItems: number; unitsTotal: number; unitsOnHand: number;
    palletsOnHand: number; palletsAllocated: number; palletsAvailable: number; modulesAvailable: number;
    value: number; utilizationPct?: number;
  }[];
  byPo: {
    lotId: string; poNumber?: string; contractNumber: string; shipTo: string;
    palletsExpected: number; palletsLoaded: number; palletsOutstanding: number;
    modulesAllocated: number; modulesRemaining: number;
  }[];
  byStatus: { status: InventoryPalletStatus; lineItems: number; units: number; value: number }[];
  logistics: { inboundLoads: number; inboundUnits: number; byStatus: { status: InventoryShipmentStatus; count: number }[] };
  demand: {
    sites: number;
    modulesRequired: number;
    modulesRequiredInProgress: number;
    modulesProcured: number;
    modulesAllocatedToSites: number;
    modulesAllocatedFromPos: number;
    modulesRemaining: number;
    surplus: number;
    complete: number;
    partial: number;
    unallocated: number;
  };
  outbound: {
    sitesWithPallets: number;
    sitesWithDelivery: number;
    byStatus: { status: InventoryDeliveryStatus; count: number }[];
    trucksRequired: number;
    palletsAllocatedNotShipped: number;
    palletsDelivered: number;
    modulesDelivered: number;
    sitesArrivingAfterMobilization: number;
  };
  flags: {
    damagedUnits: number;
    rmaLineItems: number;
    lostLineItems: number;
    serialsPendingLineItems: number;
    palletsOnInactiveSites: number;
    provisionalPallets: number;
  };
  cost: { invoicedTotal: number; invoicesUnpaid: number; balanceDue: number };
}

// --- Method declarations ---

export interface InventoryListPalletsArg {
  lotId?: string;
  warehouseId?: string;
  status?: InventoryPalletStatus;
  assigned?: 'any' | 'assigned' | 'unassigned';
  siteId?: string;
  projectManagementProjectId?: string;
  search?: string;
  skip?: number;
  limit?: number;
  sort?: { [key: string]: 1 | -1 };
}

export interface InventoryListPalletsResult {
  rows: InventoryPalletRow[];
  count: number;
  totalCount: number;
  modules: number;
  value: number;
}

export interface InventoryPalletDetailResult {
  pallet: InventoryPallet;
  lot?: InventoryLot;
  warehouse?: InventoryWarehouse;
  site?: InventorySite;
  delivery?: InventoryDelivery;
  modules: InventoryModule[];
  events: InventoryEvent[];
  flash?: { count: number; meanPmax: number; minPmax: number; maxPmax: number; belowNameplate: number };
}

export interface InventoryListSitesArg {
  projectStatus?: InventorySiteStatus;
  allocation?: InventoryAllocationStatus;
  region?: string;
  closestWarehouseId?: string;
  deliveryStatus?: InventoryDeliveryStatus | 'none';
  group?: string;
  linked?: 'any' | 'linked' | 'unlinked';
  search?: string;
  skip?: number;
  limit?: number;
}

export interface InventoryListSitesResult {
  rows: InventorySiteRow[];
  count: number;
  totals: {
    modulesRequired: number; palletsRequired: number; modulesAllocated: number; palletsAllocated: number;
    moduleBalance: number; lntpAmount: number; equipmentWoAmount: number;
  };
  regions: string[];
  groups: string[];
}

export interface InventorySiteDetailResult {
  site: InventorySite;
  row: InventorySiteRow;
  pallets: InventoryPalletRow[];
  deliveries: InventoryDeliveryRow[];
  reductions: InventoryReduction[];
  events: InventoryEvent[];
  projectName?: string;
}

export interface InventoryLotDetailResult {
  row: InventoryLotRow;
  warehouse?: InventoryWarehouse;
  reductions: InventoryReduction[];
  palletsByStatus: { status: InventoryPalletStatus; count: number; modules: number }[];
}

export interface InventoryImportResult {
  dryRun: boolean;
  fileName?: string;
  counts: { collection: string; created: number; updated: number; unchanged: number }[];
  tieOut: { label: string; imported: number | string; workbook?: number | string; ok: boolean }[];
  warnings: string[];
  errors: string[];
  millis: number;
}

export interface InventoryAssignResult {
  assigned: number;
  skipped: number;
  modules: number;
}

export interface InventoryAllocateSiteResult {
  assignedPallets: number;
  assignedModules: number;
  shortfallModules: number;
  shortfallPallets: number;
}

export const inventoryDashboard = DeclareMethod<void, InventoryDashboardData>('inventoryDashboard');
export const inventoryListPallets = DeclareMethod<InventoryListPalletsArg, InventoryListPalletsResult>('inventoryListPallets');
export const inventoryPalletDetail = DeclareMethod<{ palletId: string }, InventoryPalletDetailResult>('inventoryPalletDetail');
export const inventoryListSites = DeclareMethod<InventoryListSitesArg, InventoryListSitesResult>('inventoryListSites');
export const inventorySiteDetail = DeclareMethod<{ siteId: string }, InventorySiteDetailResult>('inventorySiteDetail');
export const inventoryListLots = DeclareMethod<void, InventoryLotRow[]>('inventoryListLots');
export const inventoryLotDetail = DeclareMethod<{ lotId: string }, InventoryLotDetailResult>('inventoryLotDetail');
export const inventoryListWarehouses = DeclareMethod<void, InventoryWarehouseRow[]>('inventoryListWarehouses');
export const inventoryWarehouseDetail = DeclareMethod<{ warehouseId: string }, { row: InventoryWarehouseRow, invoices: InventoryWarehouseInvoice[], lots: InventoryLotRow[] }>('inventoryWarehouseDetail');
export const inventoryListShipments = DeclareMethod<{ direction?: string, status?: InventoryShipmentStatus, poNumber?: string, warehouseId?: string, search?: string }, InventoryShipment[]>('inventoryListShipments');
export const inventorySearchSerials = DeclareMethod<{ search: string, palletId?: string, limit?: number }, InventoryModuleRow[]>('inventorySearchSerials');
export const inventoryListDeliveries = DeclareMethod<{ status?: InventoryDeliveryStatus, siteId?: string, search?: string }, InventoryDeliveryRow[]>('inventoryListDeliveries');
export const inventoryImportTracker = DeclareMethod<{ file: { url: string, name?: string }, dryRun?: boolean }, InventoryImportResult>('inventoryImportTracker');

// Mutations (edit tier)
export const inventoryAssignPallets = DeclareMethod<{ palletIds: string[], siteId: string, force?: boolean }, InventoryAssignResult>('inventoryAssignPallets');
export const inventoryUnassignPallets = DeclareMethod<{ palletIds: string[] }, InventoryAssignResult>('inventoryUnassignPallets');
export const inventoryAllocateSite = DeclareMethod<{ siteId: string, warehouseId?: string }, InventoryAllocateSiteResult>('inventoryAllocateSite');
export const inventorySetPalletStatus = DeclareMethod<{ palletIds: string[], status: InventoryPalletStatus, note?: string }, { updated: number }>('inventorySetPalletStatus');
export const inventoryRenamePallet = DeclareMethod<{ palletId: string, palletNumber: string }, string>('inventoryRenamePallet');
export const inventorySavePalletNotes = DeclareMethod<{ palletId: string, notes?: string, warrantyExpiration?: Date }, string>('inventorySavePalletNotes');
export const inventoryRequestDelivery = DeclareMethod<{ siteId: string, palletIds?: string[], scheduledMobilizationDate?: Date, trucks?: number, notes?: string }, string>('inventoryRequestDelivery');
export const inventorySetDeliveryStatus = DeclareMethod<{
  deliveryId: string, status: InventoryDeliveryStatus,
  actualShipDate?: Date, arrivedAt?: Date, receivedBy?: string, bol?: string, carrier?: string, note?: string,
}, { reductionsPosted: number, shipmentsWritten: number }>('inventorySetDeliveryStatus');
export const inventoryUpdateDelivery = DeclareMethod<{
  deliveryId: string, palletIds?: string[], scheduledMobilizationDate?: Date, trucks?: number, carrier?: string, bol?: string, notes?: string,
}, string>('inventoryUpdateDelivery');
export const inventoryDeleteDelivery = DeclareMethod<{ deliveryId: string }, void>('inventoryDeleteDelivery');

// --- Model classes for the plain CRUD surfaces (warehouses, lots, sites) ---

type WarehouseRefs = References<InventoryWarehouse>

export const inventoryWarehouseModelClass: ModelClass<InventoryWarehouse, WarehouseRefs> = {
  collection: InventoryWarehouses,
  isOrgOwned: true,
  editableFields: [
    'active', 'code', 'label', 'address', 'city', 'state', 'zip', 'isLaydownYard', 'operator', 'operatorProjectNumber',
    'operatorWarehouseName', 'contactName', 'contactPhone', 'capacityUnits', 'receivingWindow', 'rates',
    'scheduleAPallets', 'scheduleAMw', 'storageType', 'bonded', 'notes',
  ],
  listMethod: DeclareMethod<ListMethodArg<InventoryWarehouse>, ListMethodResponse<InventoryWarehouse, WarehouseRefs>>('inventoryListWarehouseDocs'),
  getMethod: DeclareMethod<string, GetMethodResponse<InventoryWarehouse, WarehouseRefs>>('inventoryGetWarehouse'),
  createMethod: DeclareMethod<Partial<InventoryWarehouse>, string>('inventoryCreateWarehouse'),
  updateMethod: DeclareMethod<Partial<InventoryWarehouse>, string>('inventoryUpdateWarehouse'),
  deleteMethod: DeclareMethod<string>('inventoryDeleteWarehouse'),
  pathPrefix: '/inventory/warehouses',
  icon: 'shelves',
  modelName: strings.InventoryWarehouse(),
  pluralName: strings.InventoryWarehouses(),
  asString: (document) => document.code || document.label || document._id,
  /**
   * Builds a Mongo query matching warehouses by id, code, label or city.
   * @param search - free-text search term
   * @returns Mongo selector ({} when search is empty)
   */
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { code: new RegExp(search, 'gi') },
        { label: new RegExp(search, 'gi') },
        { city: new RegExp(search, 'gi') },
        { address: new RegExp(search, 'gi') },
      ],
    };
  },
  /**
   * Validates a warehouse's fields.
   * @param warehouse - partial warehouse to validate
   * @returns map of field name to validation errors
   */
  validate: async (warehouse: Partial<InventoryWarehouse>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, warehouse, 'active');
    validateString(errors, warehouse, 'code', { required: true });
    validateString(errors, warehouse, 'label', { required: true });
    validateString(errors, warehouse, 'address', { required: true });
    validateString(errors, warehouse, 'city', { required: true });
    validateString(errors, warehouse, 'state', { required: true });
    validateString(errors, warehouse, 'zip');
    validateBoolean(errors, warehouse, 'isLaydownYard');
    validateNumber(errors, warehouse, 'capacityUnits');
    validateString(errors, warehouse, 'notes');
    return errors;
  },
};

type LotRefs = References<InventoryLot>

export const inventoryLotModelClass: ModelClass<InventoryLot, LotRefs> = {
  collection: InventoryLots,
  isOrgOwned: true,
  editableFields: [
    'active', 'poNumber', 'contractNumber', 'vendorName', 'vendorAddress', 'vendorCity', 'vendorState', 'vendorZip',
    'vendorInvoiceNumbers', 'shipToWarehouseId', 'equipmentType', 'moduleModel', 'wattage', 'modulesOrdered',
    'modulesPerPallet', 'palletsOrdered', 'pricePerWatt', 'costPerUnit', 'salesTaxRate', 'edd', 'gdd',
    'paymentTerms', 'shippingTerms', 'domesticContentPct', 'feocStatus', 'notes',
  ],
  listMethod: DeclareMethod<ListMethodArg<InventoryLot>, ListMethodResponse<InventoryLot, LotRefs>>('inventoryListLotDocs'),
  getMethod: DeclareMethod<string, GetMethodResponse<InventoryLot, LotRefs>>('inventoryGetLot'),
  createMethod: DeclareMethod<Partial<InventoryLot>, string>('inventoryCreateLot'),
  updateMethod: DeclareMethod<Partial<InventoryLot>, string>('inventoryUpdateLot'),
  deleteMethod: DeclareMethod<string>('inventoryDeleteLot'),
  pathPrefix: '/inventory/lots',
  icon: 'category',
  modelName: strings.InventoryLot(),
  pluralName: strings.InventoryLots(),
  asString: (document) => (document.poNumber ? `${strings.InventoryPo()} ${document.poNumber}` : document.contractNumber) || document._id,
  /**
   * Builds a Mongo query matching lots by id, PO number, contract or model.
   * @param search - free-text search term
   * @returns Mongo selector ({} when search is empty)
   */
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { poNumber: new RegExp(search, 'gi') },
        { contractNumber: new RegExp(search, 'gi') },
        { moduleModel: new RegExp(search, 'gi') },
        { vendorName: new RegExp(search, 'gi') },
      ],
    };
  },
  /**
   * Validates a lot's fields.
   * @param lot - partial lot to validate
   * @returns map of field name to validation errors
   */
  validate: async (lot: Partial<InventoryLot>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, lot, 'active');
    validateString(errors, lot, 'contractNumber', { required: true });
    validateString(errors, lot, 'vendorName', { required: true });
    validateString(errors, lot, 'shipToWarehouseId', { required: true });
    validateString(errors, lot, 'moduleModel', { required: true });
    validateNumber(errors, lot, 'wattage', { required: true });
    validateNumber(errors, lot, 'modulesOrdered', { required: true });
    validateNumber(errors, lot, 'modulesPerPallet', { required: true });
    validateNumber(errors, lot, 'costPerUnit');
    validateNumber(errors, lot, 'salesTaxRate');
    validateString(errors, lot, 'notes');
    return errors;
  },
};

type SiteRefs = References<InventorySite>

export const inventorySiteModelClass: ModelClass<InventorySite, SiteRefs> = {
  collection: InventorySites,
  isOrgOwned: true,
  editableFields: [
    'active', 'siteId', 'projectManagementProjectId', 'projectStatus', 'tenant', 'propertyName', 'address', 'state',
    'region', 'closestWarehouseId', 'group', 'moduleModel', 'wattage', 'lntpSizeKw', 'lntpAmount',
    'equipmentWoSizeKw', 'equipmentWoAmount', 'noEquipmentWo', 'finalDesignKw', 'designSizeOverrideKw', 'notes',
  ],
  listMethod: DeclareMethod<ListMethodArg<InventorySite>, ListMethodResponse<InventorySite, SiteRefs>>('inventoryListSiteDocs'),
  getMethod: DeclareMethod<string, GetMethodResponse<InventorySite, SiteRefs>>('inventoryGetSite'),
  createMethod: DeclareMethod<Partial<InventorySite>, string>('inventoryCreateSite'),
  updateMethod: DeclareMethod<Partial<InventorySite>, string>('inventoryUpdateSite'),
  deleteMethod: DeclareMethod<string>('inventoryDeleteSite'),
  pathPrefix: '/inventory/sites',
  icon: 'homeWork',
  modelName: strings.InventorySite(),
  pluralName: strings.InventorySites(),
  asString: (document) => document.propertyName || document.siteId || document._id,
  /**
   * Builds a Mongo query matching sites by id, site number, property, tenant or group.
   * @param search - free-text search term
   * @returns Mongo selector ({} when search is empty)
   */
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { siteId: new RegExp(search, 'gi') },
        { propertyName: new RegExp(search, 'gi') },
        { tenant: new RegExp(search, 'gi') },
        { group: new RegExp(search, 'gi') },
        { address: new RegExp(search, 'gi') },
      ],
    };
  },
  /**
   * Validates a site's fields.
   * @param site - partial site to validate
   * @returns map of field name to validation errors
   */
  validate: async (site: Partial<InventorySite>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, site, 'active');
    validateString(errors, site, 'siteId', { required: true });
    validateString(errors, site, 'propertyName', { required: true });
    validateString(errors, site, 'tenant');
    validateString(errors, site, 'address');
    validateNumber(errors, site, 'lntpSizeKw');
    validateNumber(errors, site, 'wattage');
    validateString(errors, site, 'notes');
    return errors;
  },
};

Meteor.isServer && Meteor.startup(async () => {
  await InventoryWarehouses.createIndexAsync({ orgId: 1, code: 1 }, { unique: true, sparse: true });
  await InventoryLots.createIndexAsync({ orgId: 1, contractNumber: 1 }, { unique: true, sparse: true });
  await InventoryLots.createIndexAsync({ orgId: 1, poNumber: 1 });
  await InventoryPallets.createIndexAsync({ orgId: 1, palletNumber: 1 }, { unique: true, sparse: true });
  await InventoryPallets.createIndexAsync({ lotId: 1 });
  await InventoryPallets.createIndexAsync({ warehouseId: 1, status: 1 });
  await InventoryPallets.createIndexAsync({ assignedSiteId: 1 });
  await InventoryPallets.createIndexAsync({ deliveryId: 1 });
  await InventoryModules.createIndexAsync({ orgId: 1, serial: 1 }, { unique: true, sparse: true });
  await InventoryModules.createIndexAsync({ palletId: 1, position: 1 });
  await InventorySites.createIndexAsync({ orgId: 1, siteId: 1 }, { unique: true, sparse: true });
  await InventorySites.createIndexAsync({ projectManagementProjectId: 1 });
  await InventoryDeliveries.createIndexAsync({ siteId: 1, status: 1 });
  await InventoryDeliveries.createIndexAsync({ orgId: 1, requestedAt: -1 });
  await InventoryShipments.createIndexAsync({ orgId: 1, shipmentId: 1 }, { unique: true, sparse: true });
  await InventoryShipments.createIndexAsync({ deliveryId: 1 });
  await InventoryReductions.createIndexAsync({ lotId: 1, count: 1 }, { unique: true });
  await InventoryReductions.createIndexAsync({ deliveryId: 1 });
  await InventoryReductions.createIndexAsync({ siteId: 1 });
  await InventoryWarehouseInvoices.createIndexAsync({ orgId: 1, invoiceNumber: 1 }, { unique: true, sparse: true });
  await InventoryWarehouseInvoices.createIndexAsync({ warehouseId: 1, servicePeriodStart: 1 });
  await InventoryEvents.createIndexAsync({ palletId: 1, at: -1 });
  await InventoryEvents.createIndexAsync({ siteId: 1, at: -1 });
  await InventoryEvents.createIndexAsync({ deliveryId: 1, at: -1 });

  // The first inventory module stored a flat SKU catalog with per-project assignment rows. It was never
  // populated on any environment, and the pallet model below replaces it outright; drop the empty
  // collections so a stale index or a half-remembered name cannot resurface as a second source of truth.
  try {
    const db = MongoInternals.defaultRemoteCollectionDriver().mongo.db;
    for (const name of ['projectManagementInventoryItems', 'projectManagementInventoryAssignments']) {
      const existing = await db.listCollections({ name }).toArray();
      if (!existing.length) continue;
      if (await db.collection(name).countDocuments()) continue;
      await db.dropCollection(name);
    }
  }
  catch (error: any) {
    log('error', 'legacy inventory collection cleanup failed', { error: `${error}`, stack: error?.stack }, { tags: ['startupCleanup'] });
  }
});
